'use client';

import { FormField } from '@shared/components/shadcn';
import { useTranslations } from 'next-intl';
import { useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { DateOfBirth } from '../../../../form-controls/DateOfBirth/DateOfBirth';
import { useMultistepForm } from '../../../../form-controls/MultistepForm/hooks/useMultistepForm';
import { TextInput } from '../../../../form-controls/TextInput/TextInput';
import { ContinueButton } from '../../components/ContinueButton/ContinueButton';
import { StepLayout } from '../../components/StepLayout/StepLayout';
// import { getApiValidationTranslations } from '../../RegistrationSchema';
import { useStepValidation, useValidationErrorHandler } from '../../hooks/usePlayerValidation';
import type { FormValues } from '../../types';
import { getRegistrationInternationalDialCode, transformFormDataToApiPayload } from '../../utils/payloadTransformer';

/**
 * Transforms the registration form data for personal details validation
 */
function transformPersonalDetailsPayload(formData: FormValues): Record<string, unknown> {
  // Use the shared transformation function as base
  const basePayload = transformFormDataToApiPayload(formData);

  // Override defaults for personal details validation
  return {
    ...basePayload,
    // Override empty defaults for personal details validation
    limitAmount: 0,
    limitPeriod: 'DAY',
    postCode: '',
    city: '',
    streetName: '',
    streetNr: '',
    mobilePhoneNumber: '',
    countryCode: getRegistrationInternationalDialCode(),
  };
}

function PersonalDetailsStep() {
  const t = useTranslations('form');
  const step = useTranslations('register.screens.personalDetails');
  const registerT = useTranslations('register');
  const { goToNextStep, updateFormData } = useMultistepForm();
  const form = useFormContext<FormValues>();
  const lastValidationStatusCode = useRef<number | undefined>(undefined);
  const lastValidationCaughtError = useRef<unknown>(undefined);

  const { validateWithStatus, isValidating: isLoading } = useStepValidation({
    form,
    fieldMapping: {
      firstname: 'personalDetails.firstName',
      lastname: 'personalDetails.lastName',
      birthdate: 'personalDetails.dateOfBirth',
    },
    fieldMessages: {
      'personalDetails.firstName.invalid_value': registerT('validation.personalDetails.firstName.invalid_value'),
      'personalDetails.lastName.invalid_value': registerT('validation.personalDetails.lastName.invalid_value'),
    },
    transformPayload: transformPersonalDetailsPayload,
  });

  // Error handler for server errors
  const { handleError } = useValidationErrorHandler();

  const {
    formState: { errors },
    trigger,
    getValues,
  } = form;

  const dobErrorMessages =
    errors.personalDetails?.dateOfBirth?.message ||
    errors.personalDetails?.dateOfBirth?.day?.message ||
    errors.personalDetails?.dateOfBirth?.month?.message ||
    errors.personalDetails?.dateOfBirth?.year?.message;

  // This function can be called by the parent when validating this step
  const validateAndUpdateData = async () => {
    const isFormValid = await trigger('personalDetails');

    if (isFormValid) {
      // Validate personal details with API using the reusable hook
      const {
        success: isApiValid,
        statusCode,
        caughtError,
      } = await validateWithStatus({}, undefined, () => {
        // On successful validation, update the form data
        const personalDetails = getValues('personalDetails');
        updateFormData('personalDetails', personalDetails);
      });

      // Store status code for later use in error handling
      lastValidationStatusCode.current = statusCode;
      lastValidationCaughtError.current = caughtError;

      return isApiValid;
    }

    return false;
  };

  // Handle continue button click
  const handleContinue = async () => {
    const isValid = await validateAndUpdateData();
    if (isValid) {
      goToNextStep();
    } else {
      // Handle server errors using the reusable error handler
      handleError(lastValidationStatusCode.current, lastValidationCaughtError.current);
      // For validation errors (422), just stay on the current step and show form errors
    }
  };

  return (
    <StepLayout title={step('title')} data-testid="personal-details-step">
      {/* Personal details form */}
      <div className="flex flex-col gap-8 align-center">
        <FormField
          name={'personalDetails.firstName'}
          render={({ field, fieldState }) => (
            <TextInput
              {...field}
              id="personalDetails.firstName"
              label={t('labels.firstName')}
              placeholder={t('placeholders.firstName')}
              type="text"
              helpMessage={fieldState.error?.message}
              helpMessageMode="focusout"
              helpMessageType={fieldState.error ? 'error' : undefined}
              inputTrackingProps={{
                'data-track-name': 'First Name',
                'data-track-privacy': 'Mask',
              }}
              helperTrackingProps={{
                'data-track-helper': 'First Name',
              }}
              data-testid="personal-details-form-first-name"
            />
          )}
        />

        <FormField
          name={'personalDetails.lastName'}
          render={({ field, fieldState }) => (
            <TextInput
              {...field}
              id="personalDetails.lastName"
              label={t('labels.lastName')}
              placeholder={t('placeholders.lastName')}
              type="text"
              helpMessage={fieldState.error?.message}
              helpMessageMode="focusout"
              helpMessageType={fieldState.error ? 'error' : undefined}
              inputTrackingProps={{
                'data-track-name': 'Last Name',
                'data-track-privacy': 'Mask',
              }}
              helperTrackingProps={{
                'data-track-helper': 'Last Name',
              }}
              data-testid="personal-details-form-last-name"
            />
          )}
        />

        <DateOfBirth
          formId="personalDetails.dateOfBirth"
          label={t('labels.dateOfBirth')}
          namePrefix="personalDetails.dateOfBirth"
          helpMessage={dobErrorMessages}
          helpMessageMode="focusout"
          helpMessageType={dobErrorMessages ? 'error' : 'info'}
        />
      </div>

      {/* Continue button */}
      <div className="mt-auto md:mt-0" data-track-container="Actions">
        <ContinueButton loading={isLoading} onClick={handleContinue} data-track-name="Continue" />
      </div>
    </StepLayout>
  );
}

export { PersonalDetailsStep, transformPersonalDetailsPayload };
